import {
  ROPES, SAILS, SHIP_POINTS, breachSeverity, gunLabel, repairStep, sailHoleCount, sailShredded,
  type HoldTarget, type RepairStep, type RepairableShip, type Vec3,
} from "@tiny-seas/shared";

/**
 * Client-side wording and placement of ship repairs (hold E). What needs repairing, where the
 * sailor stands, how long and how many planks all come from the shared `repairStep`; this module
 * only names the work and picks prompt anchors. Babylon-free.
 */

/** Repairable damage as replicated on `ShipState`, including hole severity. */
export interface DamagedShip extends RepairableShip {
  breaches: number;
  planks: number;
}

/** Hole names by severity (DAMAGE.maxSeverity = 4). */
export const HOLE_NAMES = ["", "sprung plank", "shot hole", "gaping hole", "stove-in hole"] as const;

/** Every repair hold target, in a fixed order (leaks, helm, guns, rigging, sails). */
export const REPAIR_TARGETS: readonly HoldTarget[] = [
  ...SHIP_POINTS.leaks.map((_, i) => `leak:${i}` as const),
  "wheel",
  ...SHIP_POINTS.cannons.map((_, i) => `cannon:${i}` as const),
  ...ROPES.map((_, i) => `rope:${i}` as const),
  ...SAILS.map((_, i) => `sail:${i}` as const),
];

export type RepairKind = "leak" | "wheel" | "cannon" | "rope" | "sail";

export function repairKind(target: HoldTarget): RepairKind | null {
  if (target === "wheel") return "wheel";
  const prefix = target.slice(0, target.indexOf(":"));
  return prefix === "leak" || prefix === "cannon" || prefix === "rope" || prefix === "sail" ? prefix : null;
}

const index = (target: string): number => Number(target.slice(target.indexOf(":") + 1));
const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? "" : "s"}`;

/** Total planks to finish `target` (every remaining step of a hole), given its next step. */
export function planksToFinish(ship: DamagedShip, target: HoldTarget, step: Readonly<RepairStep>): number {
  return target.startsWith("leak:") ? step.planks * breachSeverity(ship.leaks, ship.breaches, index(target)) : step.planks;
}

/** What the next hold on `target` does, e.g. "Splice the port main sheet" or "Patch the stove-in hole (4 planks)". */
export function repairAction(ship: DamagedShip, target: HoldTarget, step: Readonly<RepairStep>): string {
  const i = index(target), planks = planksToFinish(ship, target, step);
  const cost = planks > 0 ? ` (${plural(planks, "plank")})` : "";
  switch (repairKind(target)) {
    case "leak": return `Patch the ${HOLE_NAMES[breachSeverity(ship.leaks, ship.breaches, i)]}${cost}`;
    case "wheel": return `${ship.wheelHp <= 0 ? "Rebuild the smashed helm" : "Mend the damaged helm"}${cost}`;
    case "cannon": return `Remount the ${gunLabel(i)}${cost}`;
    case "rope": return `Splice the ${ROPES[i]!.label}`;
    case "sail": {
      const sail = SAILS[i]!.label;
      return sailShredded(ship.sailHoles, i) ? `Bend a new ${sail}` : `Patch the ${sail} (${plural(sailHoleCount(ship.sailHoles, i), "hole")})`;
    }
    default: return "Repair";
  }
}

/** The hold prompt for `target`, flagging a lack of planks aboard. */
export function repairPrompt(ship: DamagedShip, target: HoldTarget, step: Readonly<RepairStep>): string {
  const short = step.planks > ship.planks ? " · not enough planks aboard" : "";
  return `Hold E  ${repairAction(ship, target, step)}${short}`;
}

/** Progress prompt while a repair of `target` is held. */
export function repairingPrompt(ship: DamagedShip | undefined, target: HoldTarget | null): string {
  const i = target ? index(target) : -1;
  switch (target ? repairKind(target) : null) {
    case "rope": return `Splicing the ${ROPES[i]!.label}… keep holding E`;
    case "sail": return `${ship && sailShredded(ship.sailHoles, i) ? "Bending a new" : "Patching the"} ${SAILS[i]!.label}… keep holding E`;
    case "wheel": return "Rebuilding the helm… keep holding E";
    case "cannon": return `Remounting the ${gunLabel(i)}… keep holding E`;
    default: {
      const severity = ship && i >= 0 ? breachSeverity(ship.leaks, ship.breaches, i) : 0;
      return `Patching the ${severity > 0 ? HOLE_NAMES[severity] : "leak"}… keep holding E`;
    }
  }
}

/**
 * Where a prompt for `target` floats (ship-local), written into `out`: the work itself for holes,
 * the helm and guns; for rigging and sails (whose stand points can be shared, e.g. the crow's nest)
 * chest height over the stand point, nudged toward the rope's lower end.
 */
export function repairPromptPoint(target: HoldTarget, step: Readonly<RepairStep>, out: Vec3): Vec3 {
  const p = step.point;
  out.x = p.x; out.y = p.y; out.z = p.z;
  const kind = repairKind(target);
  if (kind !== "rope" && kind !== "sail") return out;
  out.y += PROMPT_LIFT;
  if (kind === "rope") {
    const lo = ROPES[index(target)]!.lines[0]![0];
    const dx = lo.x - p.x, dz = lo.z - p.z, d = Math.hypot(dx, dz);
    if (d > 1e-3) { out.x += (dx / d) * Math.min(d, PROMPT_NUDGE); out.z += (dz / d) * Math.min(d, PROMPT_NUDGE); }
  }
  return out;
}
/** Chest height over a deck stand point, and the farthest a rope prompt leans toward its rope. */
const PROMPT_LIFT = 1;
const PROMPT_NUDGE = 0.5;

const scratch: RepairStep = { point: SHIP_POINTS.wheel, seconds: 0, planks: 0 };

/**
 * The damaged part nearest ship-local (x, y, z) within `reach` of its stand point: what a sailor
 * seen repairing there is working on (hold targets are not replicated). Allocation-free.
 */
export function nearestRepair(ship: RepairableShip, x: number, y: number, z: number, reach = DAMAGE_REACH): HoldTarget | null {
  let best: HoldTarget | null = null, bestD = reach * reach;
  for (const target of REPAIR_TARGETS) {
    const step = repairStep(ship, target, scratch);
    if (!step) continue;
    const p = step.point, dy = Math.max(0, Math.abs(y - p.y) - 1);
    const d = (x - p.x) ** 2 + dy * dy + (z - p.z) ** 2;
    if (d < bestD) { bestD = d; best = target; }
  }
  return best;
}
/** A repairer stands within the server's interaction reach of the part's stand point. */
const DAMAGE_REACH = 3.5;

/** Repairs worked standing (the helm's rim, rigging and canvas at chest height); holes and guns are worked kneeling. */
export function repairStanding(target: HoldTarget | null): boolean {
  const kind = target ? repairKind(target) : null;
  return kind === "wheel" || kind === "rope" || kind === "sail";
}
